<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useRouter } from 'vue-router';
import InboxReconnectionRequired from '../../components/InboxReconnectionRequired.vue';
import { loadScript } from 'dashboard/helper/DOMHelpers';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const fbSdkLoaded = ref(false);
const isProcessing = ref(false);
const processingMessage = ref('');
const authCodeReceived = ref(false);
const authCode = ref(null);
const businessData = ref(null);
const isAuthenticating = ref(false);

const runFBInit = () => {
  window.FB.init({
    appId: window.chatwootConfig?.whatsappAppId,
    autoLogAppEvents: true,
    xfbml: true,
    version: window.chatwootConfig?.whatsappApiVersion || 'v22.0',
  });
  fbSdkLoaded.value = true;
};

const loadFacebookSdk = async () => {
  return loadScript('https://connect.facebook.net/en_US/sdk.js', {
    async: true,
    defer: true,
    crossOrigin: 'anonymous',
  });
};

const tryWhatsAppLogin = () => {
  isAuthenticating.value = true;
  processingMessage.value = t(
    'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.AUTH_PROCESSING'
  );

  window.FB.login(fbLoginCallback, {
    config_id: window.chatwootConfig?.whatsappConfigurationId,
    response_type: 'code',
    override_default_response_type: true,
    extras: {
      setup: {},
      featureType: 'whatsapp_business_app_onboarding',
      sessionInfoVersion: '3',
    },
  });
};

const fbLoginCallback = response => {
  if (response.authResponse && response.authResponse.code) {
    authCode.value = response.authResponse.code;
    authCodeReceived.value = true;
    processingMessage.value = t(
      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.WAITING_FOR_BUSINESS_INFO'
    );

    if (businessData.value) {
      completeReauthorizationFlow(businessData.value);
    }
  } else if (response.error) {
    handleReauthorizationError({ error: response.error });
  } else {
    isProcessing.value = false;
    isAuthenticating.value = false;
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CANCELLED'));
  }
};

const isValidBusinessData = businessDataLocal => {
  return (
    businessDataLocal &&
    businessDataLocal.business_id &&
    businessDataLocal.waba_id
  );
};

const handleEmbeddedSignupData = async data => {
  if (
    data.event === 'FINISH' ||
    data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
  ) {
    const businessDataLocal = data.data;

    if (isValidBusinessData(businessDataLocal)) {
      businessData.value = businessDataLocal;
      if (authCodeReceived.value && authCode.value) {
        await completeReauthorizationFlow(businessDataLocal);
      } else {
        processingMessage.value = t(
          'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.WAITING_FOR_AUTH'
        );
      }
    } else {
      handleReauthorizationError({
        error: t(
          'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.INVALID_BUSINESS_DATA'
        ),
      });
    }
  } else if (data.event === 'CANCEL') {
    handleReauthorizationCancellation();
  } else if (data.event === 'error') {
    handleReauthorizationError({
      error:
        data.error_message ||
        t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SIGNUP_ERROR'),
      error_id: data.error_id,
      session_id: data.session_id,
    });
  }
};

const handleSignupMessage = event => {
  // Validate origin for security
  if (!event.origin.endsWith('facebook.com')) return;

  try {
    let data = null;
    if (typeof event.data === 'string') {
      data = JSON.parse(event.data);
    } else if (typeof event.data === 'object' && event.data !== null) {
      data = event.data;
    }
    if (data?.type === 'WA_EMBEDDED_SIGNUP') {
      handleEmbeddedSignupData(data);
    }
  } catch {
    // Ignore non-JSON or irrelevant messages
  }
};

const completeReauthorizationFlow = async businessDataParam => {
  if (!authCodeReceived.value || !authCode.value) {
    handleReauthorizationError({
      error: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.AUTH_NOT_COMPLETED'),
    });
    return;
  }

  isProcessing.value = true;
  processingMessage.value = t(
    'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.PROCESSING'
  );

  try {
    const params = {
      code: authCode.value,
      business_id: businessDataParam.business_id,
      waba_id: businessDataParam.waba_id,
      phone_number_id: businessDataParam.phone_number_id,
      inbox_id: props.inbox.id,
    };

    await store.dispatch('inboxes/createWhatsAppEmbeddedSignup', params);

    authCode.value = null;
    useAlert(t('INBOX_MGMT.WHATSAPP_REAUTHORIZE.MESSAGE_SUCCESS'));
    // Refresh the inbox to update reauthorization status
    await store.dispatch('inboxes/show', props.inbox.id);
  } catch (error) {
    const errorMessage =
      parseAPIErrorResponse(error) ||
      t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE');
    handleReauthorizationError({ error: errorMessage });
  }
};

const handleReauthorizationError = data => {
  isProcessing.value = false;
  authCodeReceived.value = false;
  isAuthenticating.value = false;

  const errorMessage =
    data.error ||
    data.message ||
    t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE');
  useAlert(errorMessage);
};

const handleReauthorizationCancellation = () => {
  isProcessing.value = false;
  authCodeReceived.value = false;
  isAuthenticating.value = false;
};

const startReauthorization = async () => {
  try {
    await loadFacebookSdk();
    runFBInit();
    tryWhatsAppLogin();
  } catch (error) {
    handleReauthorizationError({
      error: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SDK_LOAD_ERROR'),
    });
  }
};

const setupMessageListener = () => {
  window.addEventListener('message', handleSignupMessage);
};

const cleanupMessageListener = () => {
  window.removeEventListener('message', handleSignupMessage);
};

onMounted(() => {
  setupMessageListener();
});

onBeforeUnmount(() => {
  cleanupMessageListener();
});
</script>

<template>
  <InboxReconnectionRequired
    class="mx-8 mt-5"
    @reauthorize="startReauthorization"
  />
</template>
